import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, onValue, set, push, get } from 'firebase/database';
import { app } from '../firebase';
import { distributeInitialTopics } from '../services/tutorService';
import '../styles/PlanEstudio.css';

const PlanEstudioMenu = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [planes, setPlanes] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newPlanName, setNewPlanName] = useState('');
    // Almacena un array de IDs de áreas que están expandidas en la interfaz
    const [expandedAreas, setExpandedAreas] = useState([]);
    const [selectedTemas, setSelectedTemas] = useState({});
    const [areas, setAreas] = useState([]);
    const [temas, setTemas] = useState([]);

    // Configuración del Tutor
    const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
    const [fechaExamen, setFechaExamen] = useState('');
    const [limiteTemasDiarios, setLimiteTemasDiarios] = useState(5);
    const [diasDisponibles, setDiasDisponibles] = useState('1,2,3,4,5,6,7');

    // Verificar autenticación
    useEffect(() => {
        const auth = getAuth(app);
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUser(user);
                loadUserPlans(user.uid);
                loadAreasAndTemas();
            } else {
                navigate('/login');
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [navigate]);

    // Cargar planes del usuario
    const loadUserPlans = async (userId) => {
        try {
            const db = getDatabase(app);
            const planesRef = ref(db, `planes_estudio/${userId}`);
            const snapshot = await get(planesRef);
            if (snapshot.exists()) {
                const planesData = snapshot.val();
                const planesArray = Object.entries(planesData).map(([id, plan]) => ({
                    id,
                    ...plan
                }));
                setPlanes(planesArray);
            }
        } catch (error) {
            console.error('Error cargando planes:', error);
        }
    };

    // Cargar áreas y temas
    const loadAreasAndTemas = async () => {
        try {
            const db = getDatabase(app);

            // Cargar áreas
            const areasRef = ref(db, 'areas');
            const areasSnapshot = await get(areasRef);
            let areasArray = [];
            if (areasSnapshot.exists()) {
                const areasData = areasSnapshot.val();
                areasArray = Object.entries(areasData).map(([id, area]) => ({
                    id: parseInt(id),
                    ...area
                }));
                setAreas(areasArray);
            }

            // Cargar temas
            const temasRef = ref(db, 'temas');
            const temasSnapshot = await get(temasRef);
            let temasArray = [];
            if (temasSnapshot.exists()) {
                const temasData = temasSnapshot.val();
                temasArray = Object.entries(temasData).map(([id, tema]) => ({
                    id: parseInt(id),
                    ...tema
                }));
            }

            // Cargar cuestionarios del usuario desde Firestore (incluye bóveda técnica)
            if (user) {
                const { collection, getDocs } = await import('firebase/firestore');
                const { db: firestoreDB } = await import('../firebase');
                const userQuizzesRef = collection(firestoreDB, 'user_quizzes', user.uid, 'quizzes');
                const userQuizzesSnapshot = await getDocs(userQuizzesRef);
                const userQuizzesData = userQuizzesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    isCustom: true
                })).filter(q => !q.deleted);

                const validUserTemas = userQuizzesData.map(q => ({
                    id: q.id, // usamos el ID string (Firestore)
                    nombre: q.name || q.temaName,
                    area_id: parseInt(q.areaId) || 1, // Fallback al área 1
                    isCustom: true
                }));
                temasArray = [...temasArray, ...validUserTemas];
            }
            setTemas(temasArray);

        } catch (error) {
            console.error('Error cargando áreas y temas:', error);
        }
    };

    // Crear nuevo plan
    const createPlan = async () => {
        if (!newPlanName.trim() || Object.keys(selectedTemas).length === 0) {
            alert('Por favor ingresa un nombre y selecciona al menos un tema');
            return;
        }

        try {
            const db = getDatabase(app);
            const planRef = ref(db, `planes_estudio/${user.uid}`);
            const newPlanRef = push(planRef);

            // Obtener lista plana de temas seleccionados
            const temasSeleccionadosIds = Object.values(selectedTemas).flat();

            const planConfig = {
                nombre: newPlanName,
                fechaInicio: fechaInicio,
                fechaExamen: fechaExamen,
                limiteTemasDiarios: parseInt(limiteTemasDiarios, 10),
                diasDisponibles: diasDisponibles
            };

            const temasToDistribute = temasSeleccionadosIds.map(id => ({ id, estado: 'PENDIENTE' }));
            const tutorResult = distributeInitialTopics(planConfig, temasToDistribute);

            if (!tutorResult.success && tutorResult.isCriticalDelay) {
                if (!window.confirm(tutorResult.message + '\n\n¿Deseas continuar de todos modos?')) {
                    return;
                }
            }

            const planData = {
                ...planConfig,
                temasSeleccionados: temasSeleccionadosIds,
                fechaCreacion: new Date().toISOString(),
                progreso: 0,
                temasCompletados: 0,
                totalTemas: temasSeleccionadosIds.length,
                tutorMessage: tutorResult.message
            };

            await set(newPlanRef, planData);

            // Guardar el progreso inicial con las fechas asignadas por el Tutor
            const progresoRef = ref(db, `progreso_estudio/${user.uid}/${newPlanRef.key}`);
            const progresoData = {};
            tutorResult.updatedTemas.forEach(t => {
                progresoData[t.id] = { 
                    estado: 'PENDIENTE', 
                    fechaAsignada: t.fechaAsignada, 
                    completado: false, 
                    tiempoEstudiado: 0 
                };
            });
            await set(progresoRef, progresoData);

            // Recargar planes
            loadUserPlans(user.uid);

            // Resetear formulario
            setNewPlanName('');
            setExpandedAreas([]);
            setSelectedTemas({});
            setFechaExamen('');
            setLimiteTemasDiarios(5);
            setDiasDisponibles('1,2,3,4,5,6,7');
            setShowCreateForm(false);

            alert('Plan de estudio creado exitosamente');
        } catch (error) {
            console.error('Error creando plan:', error);
            alert('Error al crear el plan de estudio');
        }
    };

    // Eliminar plan
    const deletePlan = async (planId) => {
        if (!confirm('¿Estás seguro de que quieres eliminar este plan de estudio?')) {
            return;
        }

        try {
            const db = getDatabase(app);
            const planRef = ref(db, `planes_estudio/${user.uid}/${planId}`);
            await set(planRef, null);
            loadUserPlans(user.uid);
            alert('Plan eliminado exitosamente');
        } catch (error) {
            console.error('Error eliminando plan:', error);
            alert('Error al eliminar el plan');
        }
    };

    // Toggle expansión de área
    const toggleAreaExpansion = (areaId) => {
        setExpandedAreas(prev => 
            prev.includes(areaId) ? prev.filter(id => id !== areaId) : [...prev, areaId]
        );
    };

    // Toggle selección de tema
    const toggleTemaSelection = (areaId, temaId) => {
        setSelectedTemas(prev => {
            const areaKey = areaId.toString();
            const currentTemas = prev[areaKey] || [];

            const newTemas = currentTemas.includes(temaId)
                ? currentTemas.filter(id => id !== temaId)
                : [...currentTemas, temaId];

            if (newTemas.length === 0) {
                const { [areaKey]: removed, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [areaKey]: newTemas
            };
        });
    };

    // Obtener temas de un área
    const getTemasByArea = (areaId) => {
        return temas.filter(tema => tema.area_id === areaId);
    };

    const toggleDiaSeleccion = (diaNum) => {
        const diasArray = diasDisponibles ? diasDisponibles.split(',') : [];
        if (diasArray.includes(diaNum)) {
            setDiasDisponibles(diasArray.filter(d => d !== diaNum).sort().join(','));
        } else {
            setDiasDisponibles([...diasArray, diaNum].sort().join(','));
        }
    };

    const diasSemana = [
        { num: '1', nombre: 'Lunes' },
        { num: '2', nombre: 'Martes' },
        { num: '3', nombre: 'Miércoles' },
        { num: '4', nombre: 'Jueves' },
        { num: '5', nombre: 'Viernes' },
        { num: '6', nombre: 'Sábado' },
        { num: '7', nombre: 'Domingo' }
    ];

    if (loading) {
        return <div className="plan-estudio-container"><div className="loading">Cargando planes de estudio...</div></div>;
    }

    return (
        <div className="plan-estudio-container" style={{ maxWidth: '100%', margin: 0, height: 'calc(100vh - 60px)', boxSizing: 'border-box', overflowY: 'auto' }}>
            <h1>📚 Planes de Estudio</h1>

            <div className="plan-actions">
                <button
                    className="create-plan-btn"
                    onClick={() => setShowCreateForm(!showCreateForm)}
                >
                    {showCreateForm ? 'Cancelar' : '+ Crear Nuevo Plan'}
                </button>
            </div>

            {showCreateForm && (
                <div className="create-plan-form">
                    <h3>Crear Nuevo Plan de Estudio</h3>

                        {/* Formulario Unificado */}
                        <>
                            <div className="form-group">
                                <label>Nombre del Plan:</label>
                                <input
                                    type="text"
                                    value={newPlanName}
                                    onChange={(e) => setNewPlanName(e.target.value)}
                                    placeholder="Ej: Preparación para Cabo"
                                    maxLength="100"
                                />
                            </div>

                            <div className="form-group-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>Fecha del Examen (opcional):</label>
                                    <input
                                        type="date"
                                        value={fechaExamen}
                                        onChange={(e) => setFechaExamen(e.target.value)}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: 1 }}>
                                    <label>Límite Temas Diarios:</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="20"
                                        value={limiteTemasDiarios}
                                        onChange={(e) => setLimiteTemasDiarios(e.target.value)}
                                    />
                                </div>
                            </div>
                            
                            <div className="form-group">
                                <label>Días de Estudio:</label>
                                <div className="dias-checklist" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '5px' }}>
                                    {diasSemana.map(dia => (
                                        <label key={dia.num} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.85rem' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={(diasDisponibles ? diasDisponibles.split(',') : []).includes(dia.num)} 
                                                onChange={() => toggleDiaSeleccion(dia.num)} 
                                            />
                                            {dia.nombre}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="form-group">
                                <label style={{ marginBottom: '10px', display: 'block' }}>Selecciona los temas a estudiar:</label>
                                <div className="temas-accordion" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {areas.map(area => {
                                        const temasDelArea = getTemasByArea(area.id);
                                        const isExpanded = expandedAreas.includes(area.id);
                                        const selectedTemasDelArea = selectedTemas[area.id.toString()] || [];
                                        
                                        return (
                                            <div key={area.id} className="area-group" style={{ border: '1px solid var(--outline-color)', borderRadius: '8px', overflow: 'hidden' }}>
                                                <div 
                                                    className="area-header" 
                                                    onClick={() => toggleAreaExpansion(area.id)}
                                                    style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--surface-color)', cursor: 'pointer' }}
                                                >
                                                    <h4 style={{ margin: 0, fontSize: '1rem', color: 'var(--gold-matte)' }}>{area.nombre}</h4>
                                                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                                            {selectedTemasDelArea.length}/{temasDelArea.length} seleccionados
                                                        </span>
                                                        <span style={{ color: 'var(--gold-matte)' }}>{isExpanded ? '▲' : '▼'}</span>
                                                    </div>
                                                </div>
                                                
                                                {isExpanded && (
                                                    <div className="temas-list" style={{ padding: '15px', background: 'rgba(0,0,0,0.2)', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px' }}>
                                                        {temasDelArea.map(tema => (
                                                            <div key={tema.id} className="tema-checkbox" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    id={`tema-${tema.id}`}
                                                                    checked={selectedTemasDelArea.includes(tema.id)}
                                                                    onChange={() => toggleTemaSelection(area.id, tema.id)}
                                                                    style={{ marginTop: '3px' }}
                                                                />
                                                                <label htmlFor={`tema-${tema.id}`} style={{ fontSize: '0.9rem', cursor: 'pointer', lineHeight: '1.3' }}>
                                                                    {tema.nombre}
                                                                    {tema.isCustom && <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--gold-matte)', marginTop: '2px' }}>✨ De la Bóveda/Personalizado</span>}
                                                                </label>
                                                            </div>
                                                        ))}
                                                        
                                                        <div className="area-actions" style={{ gridColumn: '1 / -1', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: '10px' }}>
                                                            <button
                                                                className="select-all-btn"
                                                                style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '4px', background: 'var(--gold-matte)', color: '#0a1a2a', border: 'none', cursor: 'pointer' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const allTemaIds = temasDelArea.map(t => t.id);
                                                                    setSelectedTemas(prev => ({
                                                                        ...prev,
                                                                        [area.id.toString()]: allTemaIds
                                                                    }));
                                                                }}
                                                            >
                                                                Seleccionar Todos
                                                            </button>
                                                            <button
                                                                className="clear-all-btn"
                                                                style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '4px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--text-muted)', cursor: 'pointer' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedTemas(prev => {
                                                                        const { [area.id.toString()]: removed, ...rest } = prev;
                                                                        return rest;
                                                                    });
                                                                }}
                                                            >
                                                                Limpiar Todos
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="selection-summary" style={{ marginTop: '20px', padding: '15px', background: 'rgba(197, 160, 89, 0.1)', borderRadius: '8px', backgroundImage: 'linear-gradient(to right, rgba(197,160,89,0.14), transparent 45%)' }}>
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                                    <strong style={{ color: 'var(--gold-matte)' }}>Total temas seleccionados:</strong> {Object.values(selectedTemas).flat().length}
                                </p>
                            </div>

                            <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '20px' }}>
                                <button className="cancel-btn" style={{ background: 'transparent', color: 'var(--text-primary)', border: '1px solid rgba(255,255,255,0.2)', padding: '10px 20px', borderRadius: '6px' }} onClick={() => setShowCreateForm(false)}>
                                    Cancelar
                                </button>
                                <button className="create-btn" style={{ background: 'linear-gradient(90deg, #c5a059, #e0c283)', color: '#0a1a2a', border: 'none', padding: '10px 25px', borderRadius: '6px', fontWeight: 'bold' }} onClick={createPlan}>
                                    Crear Plan
                                </button>
                            </div>
                        </>
                </div>
            )}

            <div className="planes-list">
                <h2>Mis Planes de Estudio</h2>

                {planes.length === 0 ? (
                    <div className="no-planes">
                        <p>No tienes planes de estudio creados aún.</p>
                        <p>Crea tu primer plan para organizar tus estudios de manera efectiva.</p>
                    </div>
                ) : (
                    <div className="planes-grid">
                        {planes.map(plan => (
                            <div key={plan.id} className="plan-card">
                                <div className="plan-header">
                                    <h3>{plan.nombre}</h3>
                                    <div className="plan-date">
                                        Creado: {new Date(plan.fechaCreacion).toLocaleDateString()}
                                    </div>
                                </div>

                                <div className="plan-stats">
                                    <div className="stat">
                                        <span className="stat-label">Progreso:</span>
                                        <span className="stat-value">{plan.progreso || 0}%</span>
                                    </div>
                                    <div className="stat">
                                        <span className="stat-label">Temas:</span>
                                        <span className="stat-value">{plan.temasCompletados || 0}/{plan.totalTemas || 0}</span>
                                    </div>
                                </div>

                                <div className="plan-progress-bar">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${plan.progreso || 0}%` }}
                                    ></div>
                                </div>

                                <div className="plan-areas">
                                    <span className="areas-label">Temas:</span>
                                    <span className="tema-count">{plan.totalTemas || 0} temas seleccionados</span>
                                </div>

                                <div className="plan-actions">
                                    <button
                                        className="study-btn"
                                        onClick={() => navigate(`/dashboard/plan-estudio/${plan.id}`)}
                                    >
                                        📖 Estudiar
                                    </button>
                                    <button
                                        className="edit-btn"
                                        onClick={() => navigate(`/dashboard/plan-estudio/editar/${plan.id}`)}
                                    >
                                        ✏️ Editar
                                    </button>
                                    <button
                                        className="delete-btn"
                                        onClick={() => deletePlan(plan.id)}
                                    >
                                        🗑️ Eliminar
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlanEstudioMenu;